import React, { useState } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';

export default function ActivityRatingSlider({ activity, currentUser, family, compact = false }) {
  // Add robust null checking for activity
  if (!activity) {
    console.warn('ActivityRatingSlider: activity prop is undefined');
    return (
      <div className="text-sm text-stone-500">
        No activity to rate
      </div>
    );
  }

  const { rateActivity } = useFamilyContext();
  
  // Safe access with fallback
  const currentUserRating = activity?.ratings?.[currentUser?.id];
  const hasUserRated = currentUserRating !== undefined;
  const [myRating, setMyRating] = useState(hasUserRated ? currentUserRating : 5);
  const [hasInteracted, setHasInteracted] = useState(hasUserRated); // Track if user has moved the slider
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRatingChange = (e) => {
    const newRating = parseInt(e.target.value);
    setMyRating(newRating);
    setHasInteracted(true); // Mark as interacted when user moves slider
  };

  // Handle touch events for better mobile interaction
  const handleTouchStart = (e) => {
    // Prevent default to avoid scrolling while sliding
    e.target.classList.add('sliding');
  };

  const handleTouchEnd = (e) => {
    e.target.classList.remove('sliding');
  };

  const handleSubmitRating = async () => {
    if (!currentUser) return;
    
    setIsSubmitting(true);
    try {
      await rateActivity(activity.id, currentUser.id, myRating);
    } catch (error) {
      console.error('Error submitting rating:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get all family members with their ratings and colors
  const familyMembers = [
    ...(family?.parents || []),
    ...(family?.kids || [])
  ];

  // Create member color assignments (consistent colors for each member)
  const memberColors = [
    'bg-emerald_emerald-500', 'bg-teal-500', 'bg-amber-500', 
    'bg-violet-500', 'bg-pink-500', 'bg-indigo-500',
    'bg-indigo-500', 'bg-orange-500'
  ];

  // Only include family members who have actually rated
  const allRatings = familyMembers.map((member, index) => ({
    id: member.id,
    rating: activity.ratings?.[member.id],
    color: member.id === currentUser?.id ? 'bg-slate-800' : memberColors[index % memberColors.length],
    name: member.id === currentUser?.id ? 'You' : member.name,
    isCurrentUser: member.id === currentUser?.id,
    avatar: member.avatar
  })).filter(member => member.rating !== undefined);

  // Calculate average rating only if there are actual ratings
  const averageRating = allRatings.length > 0 
    ? (allRatings.reduce((sum, member) => sum + member.rating, 0) / allRatings.length).toFixed(1)
    : null;

  const hasChanged = !hasUserRated || currentUserRating !== myRating;

  return (
    <div className={compact ? "space-y-4" : "space-y-6"}>
      {/* Family Ratings Section - Only show if there are ratings */}
      {averageRating && (
        <div className="flex items-center justify-center gap-8">
          {/* Family Average Circle with Rainbow Fill */}
          <div className="relative flex-shrink-0">
            {/* Rainbow Circle Background */}
            <svg width="90" height="90" className="transform -rotate-90">
              {/* Background circle */}
              <circle
                cx="45"
                cy="45"
                r="40"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="8"
              />
              {/* Rainbow progress circle */}
              <circle
                cx="45"
                cy="45"
                r="40"
                fill="none"
                stroke="url(#rainbow-gradient)"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${(averageRating / 10) * 314.16} 314.16`}
                style={{ transition: 'stroke-dasharray 0.5s ease' }}
              />
              {/* Gradient definition */}
              <defs>
                <linearGradient id="rainbow-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#ef4444" />
                  <stop offset="20%" stopColor="#f59e0b" />
                  <stop offset="40%" stopColor="#eab308" />
                  <stop offset="60%" stopColor="#22c55e" />
                  <stop offset="80%" stopColor="#06b6d4" />
                  <stop offset="100%" stopColor="#8b5cf6" />
                </linearGradient>
              </defs>
            </svg>
            
            {/* Center content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-2xl font-bold text-slate-900">{averageRating}</div>
              <div className="text-xs text-stone-600 font-medium">Family</div>
            </div>
          </div>

          {/* Individual Family Member Ratings - Compact Grid */}
          <div className="grid grid-cols-3 gap-3 max-w-48">
            {allRatings.map((member) => (
              <div key={member.id} className="flex flex-col items-center">
                {/* Avatar - Smaller */}
                <div className={`w-8 h-8 rounded-full overflow-hidden shadow-sm ${member.isCurrentUser ? 'ring-2 ring-slate-300' : ''}`}>
                  {member.avatar && member.avatar.match(/^https?:\/\//) ? (
                    <img 
                      src={member.avatar} 
                      alt={member.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className={`w-full h-full ${member.color} rounded-full flex items-center justify-center text-white text-xs font-medium`}>
                      {member.avatar || member.name?.[0] || '?'}
                    </div>
                  )}
                </div>
                {/* Rating - Smaller */}
                <div className="text-sm font-semibold text-slate-900 mt-1">{member.rating}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Your Rating Section */}
      <div className={compact ? "space-y-3" : "space-y-4"}>
        <h3 className={compact ? "text-sm font-semibold text-slate-900 text-center" : "text-base font-semibold text-slate-900 text-center"}>Your Rating</h3>
        
        <div className={compact ? "relative px-2 py-3" : "relative px-4 py-4"}>
          {/* Gradient slider track */}
          <input
            type="range"
            min="0"
            max="10"
            value={myRating}
            onChange={handleRatingChange}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleTouchStart}
            onMouseUp={handleTouchEnd}
            className={`w-full ${compact ? "h-2" : "h-3"} bg-gradient-to-r from-red-400 via-amber-400 to-emerald_emerald-400 rounded-full appearance-none cursor-pointer slider touch-slider`}
            style={{
              background: 'linear-gradient(to right, #ef4444 0%, #f59e0b 20%, #eab308 40%, #22c55e 60%, #06b6d4 80%, #8b5cf6 100%)',
              WebkitAppearance: 'none',
              touchAction: 'none'
            }}
          />
          {/* Interactive indicator - Show blank circle before interaction */}
          {!hasInteracted && (
            <div 
              className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none z-30"
              style={{ left: '50%' }} // Center the blank indicator
            >
              <div className={`bg-white border-2 border-slate-300 rounded-full flex items-center justify-center shadow-md ${compact ? "w-10 h-10" : "w-10 h-10"}`}>
                {/* Empty circle to show it's interactive */}
              </div>
            </div>
          )}
          
          {/* Rating thumb - Show number after user interacts */}
          {hasInteracted && (
            <div 
              className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none z-30"
              style={{ left: `${(myRating / 10) * 100}%` }}
            >
              <div className={`bg-slate-800 text-white font-bold rounded-full flex items-center justify-center shadow-lg border-2 border-white ${compact ? "w-10 h-10 text-xs" : "w-10 h-10 text-sm"}`}>
                {myRating}
              </div>
            </div>
          )}
        </div>

        {/* Submit button - Mobile optimized */}
        {hasChanged && (
          <button
            onClick={handleSubmitRating}
            disabled={isSubmitting}
            className={`w-full bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors font-semibold disabled:opacity-50 ${compact ? "py-2.5 px-4 text-sm min-h-[44px] mt-3" : "py-3 px-6 min-h-[48px]"}`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Rating'}
          </button>
        )}
      </div>

      <style>{`
        .touch-slider {
          /* Increase touch target for better mobile interaction */
          padding: 12px 0;
          margin: -12px 0;
          -webkit-tap-highlight-color: transparent;
          touch-action: none;
        }
        
        /* Prevent page scroll when sliding */
        .touch-slider.sliding {
          touch-action: none !important;
        }
        
        body:has(.touch-slider.sliding) {
          overflow: hidden;
          position: fixed;
          width: 100%;
        }

        .slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          height: 32px;
          width: 32px;
          background: transparent;
          cursor: pointer;
          border: none;
          outline: none;
          margin-top: -14px;
          position: relative;
          z-index: 10;
        }
        
        .slider::-moz-range-thumb {
          height: 32px;
          width: 32px;
          background: transparent;
          cursor: pointer;
          border: none;
          outline: none;
          -moz-appearance: none;
          position: relative;
          z-index: 10;
        }
        
        /* Increase thumb size on touch devices */
        @media (pointer: coarse) {
          .slider::-webkit-slider-thumb {
            height: 44px;
            width: 44px;
            margin-top: -20px;
          }
          
          .slider::-moz-range-thumb {
            height: 44px;
            width: 44px;
          }
        }

        /* Hide any focus outlines that might appear */
        .slider:focus {
          outline: none;
        }
        
        .slider:focus::-webkit-slider-thumb {
          outline: none;
          box-shadow: none;
        }
        
        /* Smooth sliding animation */
        .slider {
          transition: none;
        }
        
        /* Improve touch responsiveness on mobile */
        @media (pointer: coarse) {
          .touch-slider {
            padding: 16px 0;
            margin: -16px 0;
          }
        }
        
        /* Ensure the slider track is easily touchable */
        .slider::-webkit-slider-runnable-track {
          height: 100%;
          cursor: pointer;
        }
        
        .slider::-moz-range-track {
          height: 100%;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}